import { Alert, Link } from "@mui/material";
import { type ReactNode, useEffect, useState } from "react";
import { getPluginHealth, type PluginHealth } from "../api/userprofile";
import { formatFriendlyDate } from "../utils/date-formatting";
import { SnoozeButton, useBannerSnoozed } from "./banner-snooze";
import { useSupportReport } from "./support/use-support-report";

// Plugin health changes over hours and days; a slow refresh still clears the
// banner soon after the user fixes it, without a reload.
const REFRESH_INTERVAL_MS = 5 * 60_000;

// The views that install each host. Plain links on purpose: the config page
// picks its tab from the URL when it loads, so a fresh load lands on the view.
const ARTEMIS_SETUP = "/config/linkedin/install";
const EXTENSION_SETUP = "/config/linkedin/plugin";
const LINKEDIN_SIGN_IN = "https://www.linkedin.com/login";

/**
 * App-wide banner telling the viewer their own LinkedIn plugin has stopped
 * working — no LinkedIn outreach goes out for them until it does. Shown only
 * to that user: they are the one who can restart it or sign back in.
 */
export const PluginHealthBanner = () => {
	const [health, setHealth] = useState<PluginHealth | null>(null);

	useEffect(() => {
		let mounted = true;
		const load = () => {
			getPluginHealth()
				.then((h) => {
					if (mounted) setHealth(h);
				})
				.catch(() => {});
		};
		load();
		const id = window.setInterval(load, REFRESH_INTERVAL_MS);
		return () => {
			mounted = false;
			window.clearInterval(id);
		};
	}, []);

	// Per problem: snoozing "not running" does not hide "signed out" later.
	const bannerKey = `plugin-health:${health?.problem}`;
	const snoozed = useBannerSnoozed(bannerKey);

	if (!health?.problem || snoozed) return null;

	return (
		<PluginHealthAlert
			health={health}
			snooze={<SnoozeButton bannerKey={bannerKey} />}
		/>
	);
};

/** Nothing has ever run: that is a setup step still to do, not a breakage. */
export const pluginHealthSeverity = (health: PluginHealth): "info" | "warning" =>
	health.last_seen === null ? "info" : "warning";

/** The banner itself, for a health that has a problem — also shown to staff
 * in the navbar's active-users list, exactly as its user sees it, without the
 * snooze that only its user has. */
export const PluginHealthAlert = ({
	health,
	snooze,
}: {
	health: PluginHealth;
	snooze?: ReactNode;
}) => (
	<Alert
		severity={pluginHealthSeverity(health)}
		data-component="plugin-health-banner"
		action={snooze}
	>
		{problemText(health)}
	</Alert>
);

/** The words that say what to get or where to go are themselves the link. */
const ActionLink = ({ href, children }: { href: string; children: ReactNode }) => (
	<Link
		href={href}
		color="inherit"
		underline="always"
		fontWeight="bold"
		data-component="plugin-health-action-link"
	>
		{children}
	</Link>
);

/** Opens the support box: the fix for a LinkedIn session problem may be ours. */
const SupportLink = () => {
	const { openReport } = useSupportReport();
	return (
		<Link
			component="button"
			onClick={openReport}
			color="inherit"
			underline="always"
			fontWeight="bold"
			sx={{ verticalAlign: "baseline" }}
			data-component="plugin-health-support-link"
		>
			contact support
		</Link>
	);
};

const NO_OUTREACH = "so no LinkedIn outreach goes out.";

/** What to do, said where it has to be done: Artemis Desktop (tray
 * icon, its own Artemis and LinkedIn tabs, starts at Windows login since 1.0.11),
 * or Artemis Helper, which only runs while its browser is open
 * and takes its Artemis sign-in from Artemis signed in in that same browser. */
const problemText = (health: PluginHealth): ReactNode => {
	// No plugin has ever reached us, so we know of no host to send them to: the
	// extension is the one everybody installs — the Windows app is only for
	// people who can't run an extension at all.
	if (health.last_seen === null) {
		return (
			<>
				Artemis Helper isn't installed yet, {NO_OUTREACH}{" "}
				<ActionLink href={EXTENSION_SETUP}>Install Artemis Helper</ActionLink>{" "}
				to get started.
			</>
		);
	}
	if (health.host === "browser") {
		const browser = health.browser ?? "your browser";
		const extension = `Artemis Helper in ${browser}`;
		switch (health.problem) {
			case "odi_signed_out":
				return `${extension} is signed out of Artemis, ${NO_OUTREACH} Open Artemis in ${browser} and sign in there — Artemis Helper picks up that sign-in.`;
			case "linkedin_signed_out":
				return (
					<>
						{extension} can't see a LinkedIn sign-in, {NO_OUTREACH} Sign in
						to <ActionLink href={LINKEDIN_SIGN_IN}>linkedin.com</ActionLink>{" "}
						in {browser}.
					</>
				);
			case "linkedin_session_problem":
				return (
					<>
						{extension} has a problem with its LinkedIn session,{" "}
						{NO_OUTREACH} Sign out of{" "}
						<ActionLink href={LINKEDIN_SIGN_IN}>linkedin.com</ActionLink> in{" "}
						{browser} and sign in again. If that doesn't help,{" "}
						<SupportLink />.
					</>
				);
			default:
				return (
					<>
						{extension} hasn't run since{" "}
						{formatFriendlyDate(health.last_seen)}, {NO_OUTREACH} Open{" "}
						{browser} — Artemis Helper only works while {browser} is open.
						If it's no longer there,{" "}
						<ActionLink href={EXTENSION_SETUP}>
							install Artemis Helper again
						</ActionLink>
						.
					</>
				);
		}
	}
	// An Artemis Desktop too old to start at Windows login: installing the latest one
	// fixes that for good, and the installer opens it for the sign-in step.
	const installLatest = (
		<ActionLink href={ARTEMIS_SETUP}>Install the latest Artemis Desktop</ActionLink>
	);
	switch (health.problem) {
		case "odi_signed_out":
		case "linkedin_signed_out": {
			const tab = health.problem === "odi_signed_out" ? "Artemis" : "LinkedIn";
			return health.update_needed ? (
				<>
					Artemis Desktop is signed out of {tab}, {NO_OUTREACH}{" "}
					{installLatest} and sign in on its {tab} tab — the new version also
					starts by itself when you log in to Windows.
				</>
			) : (
				`Artemis Desktop is signed out of ${tab}, ${NO_OUTREACH} Open Artemis Desktop from its icon in the system tray, go to its ${tab} tab and sign in.`
			);
		}
		case "linkedin_session_problem":
			return (
				<>
					Artemis Desktop has a problem with its LinkedIn session,{" "}
					{NO_OUTREACH} Open Artemis Desktop from its icon in the system tray,
					go to its LinkedIn tab, sign out of LinkedIn and sign in again. If
					that doesn't help, <SupportLink />.
				</>
			);
		default: {
			const since = formatFriendlyDate(health.last_seen);
			return health.update_needed ? (
				<>
					Your version of Artemis Desktop doesn't start by itself when you log
					in to Windows, so it hasn't run since {since} and no LinkedIn
					outreach goes out. {installLatest} — it does.
				</>
			) : (
				<>
					Artemis Desktop hasn't been running since {since}, {NO_OUTREACH}{" "}
					Start it from the Windows Start menu — it then starts by itself
					whenever you log in to Windows, unless you quit it with Exit Artemis
					Desktop. If it's no longer installed,{" "}
					<ActionLink href={ARTEMIS_SETUP}>
						download Artemis Desktop again
					</ActionLink>
					.
				</>
			);
		}
	}
};
